import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ResourceSidebar.css';

// Components
import ResourceList from './components/ResourceList';
import SharedSearchInput from '../SharedSearchInput/SharedSearchInput';

// Hooks
import useResourceSidebar from './hooks/useResourceSidebar';

/**
 * @component ResourceSidebar
 * @description A modern, 3-tab sidebar for resource management.
 * Cleaned up legacy split-screen logic to strictly follow the new tab-based UI.
 * 
 * @param {Object} props
 * @param {Array} props.workers - Employees list.
 * @param {Array} props.vehicles - Vehicles list.
 * @param {Array} props.kolonnen - Kolonnen/Teams list.
 * @param {string} props.activeTab - Current active tab ('personal' | 'fahrzeuge' | 'kolonnen').
 */
export default function ResourceSidebar({
    workers = [],
    vehicles = [],
    categories = [],
    kolonnen = [],
    enabledTabs = ['personal', 'fahrzeuge', 'kolonnen'],
    activeTab = 'personal',
    onTabChange,
    assignedIds = [],
    isEditMode = false,
    isEmbedded = false,
    onToggleResource,
    onDragStart,
    onDoubleClick,
    onConflictHover,
    onConflictHoverEnd,
    onOpenConflictOverview,
    isDraggable = true,
    conflictDetection = {},
    getWorkerStatus,
    getVehicleStatus,
    interaction,
    setInteraction,
    hideAssigned = false,
    onManage
}) {
    const navigate = useNavigate();
    const {
        searchTop,
        searchBottom,
        searchFull,
        setSearchTop,
        setSearchBottom,
        setSearchFull,
        toggleGroup,
        expandedGroups
    } = useResourceSidebar({ persistenceKey: isEmbedded ? 'embedded' : 'sidebar' });

    const handleManage = (tab) => {
        if (onManage) {
            onManage(tab);
            return;
        }
        if (tab === 'personal') {
            navigate('/verwaltung/mitarbeiter');
        } else if (tab === 'fahrzeuge') {
            navigate('/verwaltung/fuhrpark', { state: { fromLeader: true } });
        } else if (tab === 'kolonnen') {
            navigate('/verwaltung/mitarbeiter', { state: { targetTab: 'kolonnen' } });
        }
    };

    const handleDragOver = () => {
        if (interaction && setInteraction && !interaction.isOverSidebar) {
            setInteraction(prev => ({ ...prev, isOverSidebar: true }));
        }
    };

    const handleDragLeave = () => {
        if (interaction && setInteraction && interaction.isOverSidebar) {
            setInteraction(prev => ({ ...prev, isOverSidebar: false }));
        }
    };

    return (
        <div 
            className={`resource-sidebar-container ${isEmbedded ? 'is-embedded' : 'is-sidebar'} ${isEditMode ? 'is-edit-mode' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
        >
            {/* Tab Switcher (Segmented Control) */}
            <div className="resource-sidebar-mode-switcher co-segmented-control">
                {enabledTabs.includes('personal') && (
                    <button
                        type="button"
                        onClick={() => onTabChange && onTabChange('personal')}
                        className={activeTab === 'personal' ? 'active' : ''}
                    >
                        Personal
                    </button>
                )}
                {enabledTabs.includes('fahrzeuge') && (
                    <button
                        type="button"
                        onClick={() => onTabChange && onTabChange('fahrzeuge')}
                        className={activeTab === 'fahrzeuge' ? 'active' : ''}
                    >
                        Fahrzeuge
                    </button>
                )}
                {enabledTabs.includes('kolonnen') && (
                    <button
                        type="button"
                        onClick={() => onTabChange && onTabChange('kolonnen')}
                        className={activeTab === 'kolonnen' ? 'active' : ''}
                    >
                        Kolonnen
                    </button>
                )}
            </div>

            <div className="resource-sidebar-content" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {/* MODE: PERSONAL */}
                {activeTab === 'personal' && (
                    <section className="sidebar-section">
                        <div className="sidebar-search">
                            <SharedSearchInput
                                placeholder="Mitarbeiter suchen..."
                                value={searchTop}
                                onChange={e => setSearchTop(e.target.value)}
                                onClear={() => setSearchTop('')}
                            />
                            <button
                                type="button"
                                className="sidebar-manage-btn"
                                onClick={() => handleManage('personal')}
                                title="Mitarbeiterstamm öffnen"
                            >
                                <span className="btn-icon">📋</span> Verwalten
                            </button>
                        </div>
                        <ResourceList
                            items={workers}
                            type="worker"
                            search={searchTop}
                            assignedIds={assignedIds}
                            isEditMode={isEditMode}
                            isDraggable={isDraggable}
                            getConflicts={conflictDetection.getWorkerConflicts}
                            getStatus={getWorkerStatus}
                            onToggle={onToggleResource}
                            onDragStart={onDragStart}
                            onDoubleClick={onDoubleClick}
                            onConflictHover={onConflictHover}
                            onConflictHoverEnd={onConflictHoverEnd}
                            onOpenConflictOverview={onOpenConflictOverview}
                            hideAssigned={hideAssigned}
                            {...conflictDetection}
                        />
                    </section>
                )}

                {/* MODE: FAHRZEUGE */}
                {activeTab === 'fahrzeuge' && (
                    <section className="sidebar-section">
                        <div className="sidebar-search">
                            <SharedSearchInput
                                placeholder="Fahrzeug suchen..."
                                value={searchBottom}
                                onChange={e => setSearchBottom(e.target.value)}
                                onClear={() => setSearchBottom('')}
                            />
                            <button
                                type="button"
                                className="sidebar-manage-btn"
                                onClick={() => handleManage('fahrzeuge')}
                                title="Fahrzeugstamm öffnen"
                            >
                                <span className="btn-icon">📋</span> Verwalten
                            </button>
                        </div>
                        <ResourceList
                            items={vehicles}
                            type="vehicle"
                            search={searchBottom}
                            categories={categories}
                            assignedIds={assignedIds}
                            isEditMode={isEditMode}
                            isDraggable={isDraggable}
                            expandedGroups={expandedGroups}
                            onToggleGroup={toggleGroup}
                            getConflicts={conflictDetection.getVehicleConflicts}
                            getStatus={getVehicleStatus}
                            onToggle={onToggleResource}
                            onDragStart={onDragStart}
                            onDoubleClick={onDoubleClick}
                            onConflictHover={onConflictHover}
                            onConflictHoverEnd={onConflictHoverEnd}
                            onOpenConflictOverview={onOpenConflictOverview}
                            hideAssigned={hideAssigned}
                            {...conflictDetection}
                        />
                    </section>
                )}

                {/* MODE: KOLONNEN */}
                {activeTab === 'kolonnen' && (
                    <section className="sidebar-section">
                        <div className="sidebar-search">
                            <SharedSearchInput
                                placeholder="Kolonnen suchen..."
                                value={searchFull}
                                onChange={e => setSearchFull(e.target.value)}
                                onClear={() => setSearchFull('')}
                            />
                            <button
                                type="button"
                                className="sidebar-manage-btn"
                                onClick={() => handleManage('kolonnen')}
                                title="Kolonnen Verwaltung öffnen"
                            >
                                <span className="btn-icon">📋</span> Verwalten
                            </button>
                        </div>
                        <ResourceList
                            items={kolonnen}
                            type="kolonne"
                            search={searchFull}
                            assignedIds={assignedIds}
                            isEditMode={isEditMode}
                            isDraggable={isDraggable}
                            getConflicts={conflictDetection.getKolonneConflicts}
                            onToggle={onToggleResource}
                            onDragStart={onDragStart}
                            onDoubleClick={onDoubleClick}
                            onConflictHover={onConflictHover}
                            onConflictHoverEnd={onConflictHoverEnd}
                            onOpenConflictOverview={onOpenConflictOverview}
                            hideAssigned={hideAssigned}
                            {...conflictDetection}
                        />
                    </section>
                )}
            </div>
        </div>
    );
}
